import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard, Package, ShoppingCart, User, Settings,
    LogOut, ChevronLeft, ChevronRight, Sun, Users, Store,
    BarChart3, FileText, CreditCard, Ticket, UserCheck, BadgePercent,
    Bell, HelpCircle, Megaphone, BookOpen, Wallet, Star, Briefcase, ClipboardList,
    Menu, X, Home
} from 'lucide-react';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import VendorOnboardingBanner from '../VendorOnboardingBanner';
import VendorOnboarding from '../VendorOnboarding';
import GlobalNotifier from './GlobalNotifier';

const DashboardLayout = ({ children, userRole = 'vendor' }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);

    const isOnboardingComplete = React.useMemo(() => {
        return localStorage.getItem('vendor_onboarding_completed') === 'true';
    }, []);
    const showBanner = userRole === 'vendor' && !isOnboardingComplete;

    const vendorLinks = [
        { name: 'Dashboard', path: '/vendor/dashboard', icon: LayoutDashboard },
        { name: 'Products', path: '/vendor/products', icon: Package },
        { name: 'Product Guide', path: '/vendor/product-guide', icon: BookOpen },
        { name: 'Orders', path: '/vendor/orders', icon: ShoppingCart },
        { name: 'Reviews', path: '/vendor/reviews', icon: Star },
        { name: 'Payments', path: '/vendor/payments', icon: Wallet },
        { name: 'Support & Updates', path: '/vendor/support', icon: HelpCircle },
        { name: 'Profile', path: '/vendor/profile', icon: User },
    ];

    const adminLinks = [
        { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
        { name: 'Users', path: '/admin/users', icon: Users },
        { name: 'Vendors', path: '/admin/vendors', icon: Store },
        { name: 'Salesmen', path: '/admin/salesmen', icon: Briefcase },
        { name: 'Products', path: '/admin/products', icon: Package },
        { name: 'Orders', path: '/admin/orders', icon: ShoppingCart },
        { name: 'Reviews', path: '/admin/reviews', icon: Star },
        { name: 'Payments', path: '/admin/payments', icon: CreditCard },
        { name: 'Subsidy Applications', path: '/admin/subsidy-orders', icon: BadgePercent },
        { name: 'Tickets', path: '/admin/tickets', icon: Ticket },
        { name: 'Vendor Support', path: '/admin/vendor-tickets', icon: Store },
        { name: 'Announcements', path: '/admin/announcements', icon: Megaphone },
        { name: 'Expert Visits', path: '/admin/expert-visits', icon: UserCheck },
        { name: 'Blogs', path: '/admin/blogs', icon: FileText },
        { name: 'Settings', path: '/admin/settings', icon: Settings },
    ];

    const salesmanLinks = [
        { name: 'Dashboard', path: '/salesman/dashboard', icon: LayoutDashboard },
        { name: 'My Assignments', path: '/salesman/assignments', icon: ClipboardList, badge: true },
        { name: 'Place Order', path: '/salesman/place-order', icon: ShoppingCart },
        { name: 'My Orders', path: '/salesman/orders', icon: Package },
    ];

    const links = userRole === 'admin' ? adminLinks : userRole === 'salesman' ? salesmanLinks : vendorLinks;

    // Bottom tab bar links for mobile (max 5 items)
    const mobileTabLinks = userRole === 'salesman' ? salesmanLinks : links.slice(0, 5);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-muted/30">
            {/* Onboarding Banner */}
            {showBanner && (
                <VendorOnboardingBanner onStartWizard={() => setShowOnboarding(true)} />
            )}
            
            {(userRole === 'admin' || userRole === 'vendor') && <GlobalNotifier role={userRole} />}

            {/* ═══════════════════════════════════════════════ */}
            {/* MOBILE TOP BAR — visible on < lg screens */}
            {/* ═══════════════════════════════════════════════ */}
            <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b z-40 flex items-center justify-between px-4 shadow-sm">
                <Link to="/" className="flex items-center gap-1.5">
                    <Sun className="w-7 h-7 text-primary" />
                    <span className="font-bold text-base">
                        <span className="text-primary">Solar</span>Savers
                    </span>
                </Link>

                <div className="flex items-center gap-2">
                    {/* Role badge */}
                    <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">
                        {userRole}
                    </span>
                    {/* Hamburger for full menu */}
                    <button
                        onClick={() => setMobileMenuOpen(true)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </header>

            {/* ═══════════════════════════════════════════════ */}
            {/* MOBILE SLIDE-OUT MENU (overlay) */}
            {/* ═══════════════════════════════════════════════ */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] lg:hidden"
                            onClick={() => setMobileMenuOpen(false)}
                        />
                        <motion.div
                            initial={{ x: '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '-100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed left-0 top-0 h-full w-72 bg-card border-r shadow-2xl z-[70] lg:hidden flex flex-col"
                        >
                            {/* Header */}
                            <div className="h-14 flex items-center justify-between px-4 border-b">
                                <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                                    <Sun className="w-7 h-7 text-primary" />
                                    <span className="font-bold text-lg">
                                        <span className="text-primary">Solar</span>Savers
                                    </span>
                                </Link>
                                <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-muted rounded-lg">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* User Info */}
                            <div className="p-4 border-b bg-muted/20">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm">{user?.name || 'User'}</p>
                                        <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Nav Links */}
                            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                                {links.map((link) => {
                                    const isActive = location.pathname === link.path;
                                    return (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-sm ${isActive
                                                ? 'bg-primary text-primary-foreground font-semibold'
                                                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                }`}
                                        >
                                            <link.icon className="w-5 h-5 flex-shrink-0" />
                                            <span>{link.name}</span>
                                        </Link>
                                    );
                                })}
                            </nav>

                            {/* Logout */}
                            <div className="p-3 border-t">
                                <Button
                                    variant="ghost"
                                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 py-3"
                                >
                                    <LogOut className="w-5 h-5 mr-2" /> Logout
                                </Button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ═══════════════════════════════════════════════ */}
            {/* MOBILE BOTTOM TAB BAR — visible on < lg screens */}
            {/* ═══════════════════════════════════════════════ */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t z-40 safe-area-bottom">
                <div className="flex items-center justify-around h-16 px-1">
                    {mobileTabLinks.map((link) => {
                        const isActive = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`flex flex-col items-center gap-0.5 flex-1 py-2 rounded-lg transition-all ${
                                    isActive
                                        ? 'text-primary'
                                        : 'text-muted-foreground'
                                }`}
                            >
                                <link.icon className={`w-5 h-5 ${isActive ? 'text-primary' : ''}`} />
                                <span className={`text-[10px] font-medium leading-tight text-center ${isActive ? 'font-bold' : ''}`}>
                                    {link.name.replace('My ', '').replace('Place ', '')}
                                </span>
                                {isActive && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="w-5 h-0.5 bg-primary rounded-full mt-0.5"
                                    />
                                )}
                            </Link>
                        );
                    })}
                </div>
            </nav>

            {/* ═══════════════════════════════════════════════ */}
            {/* DESKTOP SIDEBAR — visible on >= lg screens */}
            {/* ═══════════════════════════════════════════════ */}
            <motion.aside
                initial={false}
                animate={{ width: collapsed ? 80 : 260 }}
                className="hidden lg:flex fixed left-0 top-0 h-full bg-card border-r shadow-lg z-40 flex-col"
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b">
                    {!collapsed && (
                        <Link to="/" className="flex items-center gap-2">
                            <Sun className="w-8 h-8 text-primary" />
                            <span className="font-bold text-lg">
                                <span className="text-primary">Solar</span>Savers
                            </span>
                        </Link>
                    )}
                    {collapsed && <Sun className="w-8 h-8 text-primary mx-auto" />}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCollapsed(!collapsed)}
                        className="ml-auto"
                    >
                        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </Button>
                </div>

                {/* User Info */}
                <div className={`p-4 border-b ${collapsed ? 'text-center' : ''}`}>
                    <div className={`w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center ${collapsed ? 'mx-auto' : ''}`}>
                        <User className="w-5 h-5 text-primary" />
                    </div>
                    {!collapsed && (
                        <div className="mt-2">
                            <p className="font-semibold text-sm">{user?.name || 'User'}</p>
                            <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    {links.map((link) => {
                        const isActive = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                    } ${collapsed ? 'justify-center' : ''}`}
                            >
                                <link.icon className="w-5 h-5 flex-shrink-0" />
                                {!collapsed && <span className="font-medium">{link.name}</span>}
                            </Link>
                        );
                    })}
                </nav>

                {/* Logout */}
                <div className="p-4 border-t">
                    <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className={`w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 ${collapsed ? 'justify-center px-0' : ''}`}
                    >
                        <LogOut className="w-5 h-5" />
                        {!collapsed && <span className="ml-2">Logout</span>}
                    </Button>
                </div>
            </motion.aside>

            {/* ═══════════════════════════════════════════════ */}
            {/* MAIN CONTENT */}
            {/* ═══════════════════════════════════════════════ */}
            <main
                className={`flex-1 transition-all duration-300
                    ${showBanner ? 'pt-16' : ''}
                    pt-14 pb-20 lg:pt-0 lg:pb-0
                    ${collapsed ? 'lg:ml-20' : 'lg:ml-[260px]'}`}
            >
                <div className="p-4 md:p-6 lg:p-8">
                    {children}
                </div>
            </main>

            {/* Onboarding Wizard Overlay */}
            {userRole === 'vendor' && showOnboarding && (
                <VendorOnboarding
                    onComplete={() => setShowOnboarding(false)}
                    vendorName={user?.name}
                />
            )}
        </div>
    );
};

export default DashboardLayout;
